@if (loggedIn) {
  <cs-header [backButton]="false" [fullWidth]="true">
    @if (shouldShowSettingsButton()) {
      <cs-icon-button header-left icon="menu"
      (click)="onSettingsClick()"></cs-icon-button>
    }
    <cs-header-title centered>{{ LL.subscribe.title() }}</cs-header-title>
  </cs-header>
}

<cs-content>
  <cs-card-list narrow>
    <!-- Logo -->
    <cs-info-card type="translucent" [centered]="true" [dismissible]="false">
      <img class="logo" src="assets/imgs/logo.svg" />
    </cs-info-card>

    <!-- Payment Waiting Card -->
    @if (previousCheckoutWaiting) {
      <cs-card class="payment visible">
        <div class="payment-waiting">
          {{ LL.subscribe.messages.paymentInitiated() }}<br>
          {{ LL.subscribe.messages.confirmPayment() }}<a (click)="onReloadAfterWaitingClicked()">{{ LL.subscribe.links.reload() }}</a>
          {{ LL.subscribe.messages.thisPage() }}<br>
          {{previousCheckoutWaitingTime}}<br>
          {{ LL.subscribe.messages.paymentIssues() }}<a [href]="supportMailUrl">{{supportEmail}}</a>.
        </div>
      </cs-card>
    }

    <!-- Error States -->
    <!-- ONE warning card, never two. These three used to be independent @if
         blocks, so a coach-lapsed client who also had a stored checkout error
         would have seen a stack of them. Same mutual-exclusion shape the update
         / what's-new cards use. The coach message goes first because it is the
         specific explanation for exactly this user, and the generic
         "your subscription is inactive" line is what it replaces. (#4393) -->
    @if (coachLapsed) {
      <cs-info-card id="subscribe-coach-lapsed-warning" type="warning" [dismissible]="false">
        <strong>{{ LL.subscribe.messages.coachLapsedTitle() }}</strong><br>
        @if (coachLapsedCoachName) {
          {{ LL.subscribe.messages.coachLapsed({ coachName: coachLapsedCoachName }) }}
        } @else {
          {{ LL.subscribe.messages.coachLapsedNoName() }}
        }
        <br>
        {{ LL.subscribe.messages.coachLapsedStillConnected() }}<br>
        {{ LL.subscribe.messages.coachLapsedSubscribe() }}
      </cs-info-card>
    } @else if (expired && !previousCheckoutError) {
      <cs-info-card id="subscribe-generic-warning" type="warning" [dismissible]="false">
        {{ LL.subscribe.messages.subscriptionInactive() }}<br>
        {{ LL.subscribe.messages.paymentIssues() }}<a [href]="supportMailUrl">{{supportEmail}}</a>.
      </cs-info-card>
    } @else if (previousCheckoutError) {
      <cs-info-card type="warning" [dismissible]="false">
        {{previousCheckoutError}}<br>
        {{ LL.subscribe.messages.ifPersists() }}<a [href]="supportMailUrl">{{supportEmail}}</a>.
      </cs-info-card>
    }

    <!-- Main Subscribe Card -->
    <cs-card class="subscribe-card">
      @if (loadingPrices || (!isNativePlatform && loadingCheckout) || purchaseInProgress || waitingForSubscriptionRecord) {
        <div class="spinner overlay">
          <cs-spinner></cs-spinner>
        </div>
      }

      @if (unlimitedLicense) {
        <div class="expiry-date">
          <span>{{ LL.subscribe.messages.noExpiration() }}</span>
        </div>
      }

      <!-- Cross-platform subscription message -->
      <!-- paused takes precedence: on a store platform a paused Paddle sub also
           trips the cross-platform block, whose copy ("your subscription is
           active, use the device you subscribed on") is plainly wrong for a
           paused sub. Show one message, and the accurate one. -->
      @if (pausedMembership) {
      <div class="cross-platform-message">
        <p>{{ LL.subscribe.messages.membershipPaused() }}</p>
      </div>
      } @else if (crossPlatformSubscription) {
        <div class="cross-platform-message">
          <cs-icon name="checkmark-circle"></cs-icon>
          <p>{{ crossPlatformIsWeb ? LL.subscribe.messages.crossPlatformWeb() : LL.subscribe.messages.crossPlatform() }}</p>
        </div>
      }

      <!-- Select Subscription Section -->
      <div class="select-subscription-section" [ngClass]="{ hidden: !canOfferPurchase }">
        <!-- #6145: say up front that the checkout starts with a free trial, but only when the
             prices this checkout was built with really carry one (see webTrialText) -->
        @if (webTrialText; as trial) {
          <h2 class="section-title" id="subscribe-trial-title">{{ LL.subscribe.titles.tryFree({ period: trial }) }}</h2>
          <p class="section-description">{{ LL.subscribe.notes.billingInfoTrial({ period: trial }) }}</p>
        } @else {
          <h2 class="section-title">{{ LL.subscribe.titles.selectSubscription() }}</h2>
          <p class="section-description">{{ LL.subscribe.notes.billingInfo() }}</p>
        }

        <!-- #6984: what a code applied, above the plans it changes: the sale (with Remove
             Code on its own line) and the friend's free month. Web only: the native store
             screens cannot apply a sale or a free month. -->
        @if (!isNativePlatform && !loadingPrices && promoAppliedText; as appliedText) {
          <cs-info-card class="code-applied-card" type="checkmark" [dismissible]="false" id="subscribe-promo-applied">
            <span>{{ appliedText }}</span>
            <button class="link code-remove" id="subscribe-promo-remove" [disabled]="codeSubmitting"
              (click)="removePromoCodeAsync()">{{ LL.subscribe.code.removeCode() }}</button>
          </cs-info-card>
        }
        @if (!isNativePlatform && !loadingPrices && referralFreeMonthApplied) {
          <cs-info-card class="code-applied-card" type="checkmark" [dismissible]="false" id="subscribe-referral-free-month">
            {{ LL.subscribe.code.friendApplied() }}
          </cs-info-card>
        }

        <!-- Native (Google Play) plan selection -->
        @if (isNativePlatform) {
          <cs-radio-group class="plan-options-list" [(selected)]="selectedPlan" (selectedChange)="handleNativeSelect($event)">
            @for (product of iapProducts; track product; let i = $index) {
              <cs-radio [value]="i">
                <div custom-content class="plan-content">
                  <div class="plan-left">
                    <span class="plan-name">{{product.title}}</span>
                  </div>
                  <div class="plan-right">
                    <div class="plan-prices">
                      @if (product.preDiscount) {
                        <span class="pre-discount">{{product.preDiscount}}</span>
                      }
                      <span class="price">{{product.priceText}}</span>
                    </div>
                  </div>
                </div>
                @if (product.saving) {
                  <div custom-content class="plan-corner-badge">
                    <cs-badge class="green saving-badge">{{ LL.subscribe.labels.savePrefix() }}
                      <strong>{{product.saving}}</strong></cs-badge>
                  </div>
                }
                <!-- Apple 3.1.2(c) trial-eligible plan disclosure; see trialDisclosureFor in subscribe.ts. -->
                @if (trialDisclosureFor(product); as trialLine) {
                  <div custom-content class="plan-trial-disclosure">{{ trialLine }}</div>
                }
              </cs-radio>
            }
          </cs-radio-group>
        }

        <!-- Web (PayPro) plan selection -->
        @if (!isNativePlatform) {
          <cs-radio-group class="plan-options-list" [(selected)]="selectedPlan" (selectedChange)="handleSelect($event)">
            @for (plan of planOptions; track plan; let i = $index) {
              <cs-radio [value]="i">
                <div custom-content class="plan-content">
                  <div class="plan-left">
                    <span class="plan-name">{{plan.title}}</span>
                  </div>
                  @if (plan.priceText) {
                    <div class="plan-right">
                      <div class="plan-prices">
                        @if (plan.preDiscount) {
                          <span class="pre-discount">{{plan.preDiscount}}</span>
                        }
                        <span class="price">{{plan.priceText}}</span>
                      </div>
                    </div>
                  }
                </div>
                @if (plan.saving) {
                  <div custom-content class="plan-corner-badge">
                    <cs-badge class="green saving-badge">{{ LL.subscribe.labels.savePrefix() }}
                      <strong>{{plan.saving}}</strong></cs-badge>
                  </div>
                }
                <!-- #6965: a plan on sale: the badge, the price per month and how long
                     the sale price lasts. The crossed-out regular price and the sale
                     price are preDiscount and priceText above. -->
                <!-- two separate @if blocks: a block with more than one root node
                     cannot project into the radio's [custom-content] slot (NG8011) -->
                @if (plan.promo?.percentOff; as percentOff) {
                  <div custom-content class="plan-corner-badge">
                    <cs-badge class="red promo-badge">{{ LL.subscribe.promo.percentOff({ percent: percentOff }) }}</cs-badge>
                  </div>
                }
                @if (plan.promo; as promo) {
                  <div custom-content class="plan-promo-details">
                    @if (promo.priced && promo.perMonthText) {
                      <span class="promo-per-month">{{ LL.subscribe.promo.perMonth({ price: promo.perMonthText }) }}</span>
                    }
                    <span class="promo-then">{{ promoThenText(plan) }}</span>
                  </div>
                }
                <!-- with a sale the plans can carry different trials, so each card says its own -->
                @if (planTrialText(plan); as trialLine) {
                  <div custom-content class="plan-trial-disclosure">{{ trialLine }}</div>
                }
                <!-- the referral free month when it covers this plan only (a code took the other) -->
                @if (!isNativePlatform && plan.referralFreeMonth && !referralFreeMonthApplied) {
                  <div custom-content class="plan-trial-disclosure">{{ LL.referral.subscribe.freeMonthApplied() }}</div>
                }
                </cs-radio>
              }
            </cs-radio-group>
          }

        <!-- #6984: the one code field, under the web plans, shared with the Android
             purchase card through codeFieldTemplate. Hidden while a sale code is applied:
             the green card above the plans names it, with its own Remove Code. -->
        @if (!isNativePlatform && !loadingPrices && promoNotice !== PromoNotice.Applied) {
          <div class="code-field-section">
            <ng-container *ngTemplateOutlet="codeFieldTemplate"></ng-container>
          </div>
        }

          @if (promoMoneyBackText; as moneyBack) {
            <p class="promo-money-back" id="subscribe-promo-money-back">{{ moneyBack }}</p>
          }

        </div>

        @if (!loadingPrices && !loggedIn) {
          <div class="subscribe-footer">
            <span class="footer-text">{{ LL.subscribe.messages.alreadyHaveAccountPrefix() }} </span>
            <a class="footer-link" tappable (click)="handleSignIn()">{{ LL.subscribe.messages.signIn() }}</a>
          </div>
        }
      </cs-card>

      <!-- name the account the user is on, so someone who signed in under a
           second identity can see why they are asked to pay (#5894) -->
      @if (loggedIn && (signedInEmail || canOfferPurchase)) {
        <cs-info-card class="signed-in-account" [dismissible]="false">
          @if (signedInEmail) {
            <div id="subscribe-signed-in-as">
              {{ LL.subscribe.messages.signedInAsPrefix() }}
              <strong class="signed-in-email">{{ signedInEmail }}</strong>{{ LL.subscribe.messages.signedInAsSuffix() }}
            </div>
          }
          @if (canOfferPurchase) {
            <div>
              {{ LL.subscribe.messages.alreadyHaveSubscriptionPrefix() }}
              <a tappable (click)="handleSendSignInLink()">{{ LL.subscribe.messages.alreadyHaveSubscriptionLink() }}</a>
              {{ LL.subscribe.messages.alreadyHaveSubscriptionSuffix() }}
            </div>
          }
        </cs-info-card>
      }

      <!-- Native Purchase Button -->
      <!-- also gated on !crossPlatformSubscription: an active membership billed
           on another platform must not be offered a second store purchase here
           either. The web cards above have always had that guard implicitly
           (crossPlatformSubscription hides the plan picker they depend on); this
           native card never did. -->
      @if (isNativePlatform && iapProducts.length > 0 && canOfferPurchase) {
        <cs-card class="payment-card">
          @if (isIOSNative) {
            <div class="subscription-terms">
              {{ LL.subscribe.subscriptionTerms() }}
              <a [href]="privacyUrl" target="_blank" rel="noopener" (click)="handleLegalLink($event, privacyUrl)">{{ LL.subscribe.privacyPolicy() }}</a>
              &middot;
              <a [href]="eulaUrl" target="_blank" rel="noopener" (click)="handleLegalLink($event, eulaUrl)">{{ LL.subscribe.eula() }}</a>
            </div>
          }
          <cs-button primary medium fit-width
            [label]="selectedIAPProduct?.hasTrial ? LL.subscribe.titles.startFreeTrial() : LL.subscribe.titles.subscribe()"
            [disabled]="purchaseInProgress"
            (click)="handleNativePurchase()"></cs-button>
          @if (isIOSNative) {
            <cs-button secondary small fit-width
              [disabled]="purchaseInProgress"
              [label]="LL.subscribe.restorePurchases()"
              (click)="handleRestorePurchases()"></cs-button>
          }
          <!-- #6984: the code field takes access codes only in the Android app, and iOS
               shows no code entry at all, to comply with Apple 3.1.1 (subscriptions
               must be activated via IAP, not codes). -->
          @if (loggedIn && !isIOSNative) {
            <div class="code-field-section native">
              <ng-container *ngTemplateOutlet="codeFieldTemplate"></ng-container>
            </div>
          }
        </cs-card>
      }

      <!-- No store products (#3969): the native store finished loading but
           returned no purchasable products (app not installed from Google Play,
           Play billing unavailable, etc.). Show an actionable message instead of
           a blank paywall. The condition is reported to Sentry in
           SubscribeNativeRail.fetchPricesAsync; shouldShowNoStoreProductsMessage()
           gates this block. -->
      @if (shouldShowNoStoreProductsMessage()) {
        <cs-card class="payment-card no-store-products">
          <cs-icon name="warning"></cs-icon>
          <h3 class="section-title">{{ LL.subscribe.messages.noStoreProductsTitle() }}</h3>
          <p class="section-description">{{ LL.subscribe.messages.noStoreProductsBody() }}</p>
          <a class="footer-link" [href]="supportMailUrl">{{ supportEmail }}</a>
        </cs-card>
      }

      <!-- Every purchase surface on this page — the two web cards below, the
           native card and the plan picker above — is gated on the ONE
           canOfferPurchase getter. Hiding the plan picker is not enough: the
           price load runs from ngOnInit and selects plan 0, which mounts a live
           checkout, so a paused member would read "resume instead of buying"
           with a working, card-on-file checkout directly underneath. Completing
           it charges them a second time AND the webhook dup guard then cancels
           their paused subscription, losing both. (#4189) -->
      <!-- PayPro Payment Card (web only) -->
      @if (!isNativePlatform && webProvider !== 'paddle' && currentBuyUrl!==undefined && canOfferPurchase) {
        <cs-card class="payment-card">
          <h3 class="section-title">{{ LL.subscribe.titles.payment() }}</h3>
          <iframe class="checkout-frame" [src]="currentBuyUrl" [attr.title]="LL.subscribe.titles.payment()"
          [attr.style]="sanitizer.bypassSecurityTrustStyle('height: '+ checkoutFrameHeight  + 'px')"></iframe>
        </cs-card>
      }

      <!-- Paddle Payment Card (web only). Shown once a plan is selected (parity
      with the PPG card); the inline frame mounts into `.paddle-checkout-frame`
      (see openInlineCheckout's frameTarget), and the frame-ready poll waits for
      this container to render. -->
      @if (!isNativePlatform && webProvider === 'paddle' && selectedPlan !== undefined && canOfferPurchase) {
        <cs-card class="payment-card">
          <!-- no "Payment" title here: the Paddle inline frame has its own
          heading, so a title would be redundant and overlap the frame. -->
          <div class="paddle-checkout-frame checkout-frame"></div>
          <!-- once payment succeeds Paddle shows its success screen in the frame
          above; surface our own Continue button so the buyer reads that success
          and taps to proceed. Continue runs the license check and reloads (see
          onContinueAfterPaddlePurchase) — we never force-reload out from under
          the success screen. -->
          @if (paddlePurchaseComplete) {
            <div class="payment-footer">
              <cs-button medium primary fit-width id="subscribe-continue-paddle" [label]="LL.subscribe.messages.continueAfterPurchase()"
              (click)="onContinueAfterPaddlePurchase()"></cs-button>
            </div>
          }
        </cs-card>
      }

      <!-- Legal footer, attributed to MH Physique so it reads as ours and is
           clearly distinct from the Paddle checkout card's own "Sold by Paddle
           ... Terms / Privacy" footer above. The consent line (naming MH
           Physique + Terms + Privacy) shows on every platform. Pricing +
           Refunds describe the Paddle web checkout, so they are web-only: on
           native (iOS / Android) payments go through Apple / Google, where
           Paddle refunds and web pricing do not apply. -->
      <cs-card-list-row class="subscribe-legal-consent-row">
        <cs-note>
          {{ LL.auth.bySubscribing() }}<br><a [href]="tosUrl" target="_blank" rel="noopener" (click)="handleLegalLink($event, tosUrl)">{{ LL.auth.termsAndConditions() }}</a> {{ LL.auth.and() }} <a [href]="privacyUrl" target="_blank" rel="noopener" (click)="handleLegalLink($event, privacyUrl)">{{ LL.auth.privacyPolicy() }}</a>.
        </cs-note>
      </cs-card-list-row>
      @if (!isNativePlatform) {
        <cs-card-list-row class="subscribe-legal-footer-divider-row">
          <div class="subscribe-footer-divider"></div>
        </cs-card-list-row>
        <cs-card-list-row class="subscribe-legal-footer-row">
          <cs-note>
            <nav class="subscribe-legal-footer">
              <a href="/subscribe">{{ LL.auth.pricing() }}</a>
              <a [href]="refundUrl" target="_blank" rel="noopener" (click)="handleLegalLink($event, refundUrl)">{{ LL.auth.refunds() }}</a>
            </nav>
          </cs-note>
        </cs-card-list-row>
      }
    </cs-card-list>
    <!-- Spacer to prevent overlap with fixed version info -->
    <div class="version-spacer">
    </div>
  </cs-content>
  <cs-version-display></cs-version-display>

<!-- #6984: the one "Have a code?" field, closed behind a link until tapped. Used under the
     web plans and on the Android purchase card (access codes only there). What Apply does
     is decided in subscribe-code-field.ts. -->
<ng-template #codeFieldTemplate>
  @if (!codeFieldShown) {
    <cs-button tertiary id="subscribe-code-link" [label]="LL.subscribe.promo.haveCode()"
      (click)="openCodeField()"></cs-button>
  } @else {
    <div class="code-entry">
      <p class="code-entry-title">{{ LL.subscribe.promo.haveCode() }}</p>
      <p class="code-entry-hint">{{ isNativePlatform ? LL.subscribe.code.hintApp() : LL.subscribe.code.hint() }}</p>
      <!-- the same line the invite landing page and the Settings box carry (#4963 phase 3):
           every route that claims a referral must say what the inviter will see -->
      @if (!isNativePlatform && codeLooksLikeFriendCode) {
        <p class="referral-code-subtitle" id="subscribe-referral-disclosure">{{ LL.referral.disclosure() }}</p>
      }
      <!-- Apply sits beside the input, as in the approved mock-up (#6984) -->
      <div class="code-entry-row">
        <cs-input id="subscribe-code-input" [(value)]="codeInput" [class.error]="!!codeErrorText"
          [placeholder]="LL.subscribe.promo.codePlaceholder()"></cs-input>
        <cs-button secondary medium id="subscribe-code-apply"
          [disabled]="codeSubmitting || !codeInput"
          (click)="applyCodeAsync()"
          [label]="LL.subscribe.promo.applyButton()"></cs-button>
      </div>
      @if (codeErrorText; as errorText) {
        <p class="code-error" id="subscribe-code-error">{{ errorText }}</p>
      }
      @if (!saleMessagesDismissed) {
        @if (promoCodeError) {
          <p class="code-error" id="subscribe-promo-check-failed">{{ promoCodeError }}</p>
        }
        @switch (promoNotice) {
          @case (PromoNotice.Expired) {
            <p class="code-error" id="subscribe-promo-expired">{{ LL.subscribe.promo.expired() }}</p>
          }
          @case (PromoNotice.Ended) {
            <p class="code-error" id="subscribe-promo-ended">{{ LL.subscribe.promo.ended() }}</p>
          }
        }
      }
      @if (codeSignInPrompt; as prompt) {
        <cs-info-card type="info" [dismissible]="false" id="subscribe-code-sign-in"
          [actionText]="LL.subscribe.messages.signIn()" (actionClick)="handleSignIn()">{{ prompt }}</cs-info-card>
      }
    </div>
  }
</ng-template>
